/**
 * F6. Уличная музыка: с 14 до 30 лет при фокусе «хобби» три года подряд
 * (`flags.hobbyYears`, считает тик). Ритм-игра 10 секунд: удары по
 * четырём точкам, тап засчитан в окне ±150 мс. Деньги +5 % ВВП на душу
 * и счастье +3 независимо от точности; точность меняет только текст о
 * толпе. Азарта нет: тяга 0, «пропустить» даёт то же самое.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import { FLAG_HOBBY_YEARS, type Life, type MinigameOutcome } from '@life/engine';
import type { MinigameDef } from '../types';
import { gdpPpp } from '../casino/common';
import { nothing } from './common';

export const BUSKING_MIN_AGE = 14;
export const BUSKING_MAX_AGE = 30;
export const BUSKING_MIN_HOBBY_YEARS = 3;
export const BUSKING_GDP_SHARE = 0.05;
export const BUSKING_HAPPINESS = 3;
export const BUSKING_DURATION_MS = 10_000;
export const BUSKING_POINTS = 4;
/** Удары: с 1,5 с каждые 1 с, восемь штук. */
export const BUSKING_BEAT_MS = 1000;
export const BUSKING_FIRST_BEAT_MS = 1500;
export const BUSKING_BEATS = 8;
export const BUSKING_WINDOW_MS = 150;
/** Точность, с которой толпа «средняя» и «большая». */
export const BUSKING_CROWD_MID = 0.4;
export const BUSKING_CROWD_BIG = 0.7;

export interface BuskingState {
  /** Моменты ударов от старта, мс. */
  beats: number[];
  /** Окно попадания ±мс. */
  window: number;
  points: number;
  durationMs: number;
  gdp: number;
  amount: number;
}

export type BuskingInput = { taps: number[] };

/** Доля ударов, в окно которых попал хотя бы один тап (каждый тап — один раз). */
export function buskingAccuracy(state: BuskingState, taps: readonly number[]): number {
  if (state.beats.length === 0) return 0;
  const free = (Array.isArray(taps) ? taps : []).filter((t) => typeof t === 'number' && Number.isFinite(t)).sort((a, b) => a - b);
  let hit = 0;
  for (const beat of state.beats) {
    const i = free.findIndex((t) => Math.abs(t - beat) <= state.window);
    if (i >= 0) {
      hit += 1;
      free.splice(i, 1);
    }
  }
  return hit / state.beats.length;
}

function crowdKey(accuracy: number): string {
  if (accuracy >= BUSKING_CROWD_BIG) return 'minigame.busking.crowd.big';
  if (accuracy >= BUSKING_CROWD_MID) return 'minigame.busking.crowd.mid';
  return 'minigame.busking.crowd.small';
}

function payout(state: BuskingState, accuracy: number): MinigameOutcome {
  return {
    ...nothing(),
    moneyDelta: state.amount,
    stats: { happiness: BUSKING_HAPPINESS },
    logKey: crowdKey(accuracy),
    logParams: { amount: Math.round(state.amount) },
  };
}

export const busking: MinigameDef<BuskingInput, BuskingState> = {
  id: 'busking',
  section: 'F',
  triggers: [{ kind: 'age', min: BUSKING_MIN_AGE, max: BUSKING_MAX_AGE, everyYears: 1 }],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    if (life.age < BUSKING_MIN_AGE || life.age > BUSKING_MAX_AGE) return false;
    const years = life.flags[FLAG_HOBBY_YEARS];
    return typeof years === 'number' && years >= BUSKING_MIN_HOBBY_YEARS;
  },
  setup(life: Life, ds: Dataset): BuskingState {
    const gdp = Math.max(0, gdpPpp(ds, life));
    const beats: number[] = [];
    for (let i = 0; i < BUSKING_BEATS; i += 1) beats.push(BUSKING_FIRST_BEAT_MS + i * BUSKING_BEAT_MS);
    return { beats, window: BUSKING_WINDOW_MS, points: BUSKING_POINTS, durationMs: BUSKING_DURATION_MS, gdp, amount: gdp * BUSKING_GDP_SHARE };
  },
  play(state: BuskingState, input: BuskingInput, _life: Life): MinigameOutcome {
    return payout(state, buskingAccuracy(state, input.taps));
  },
  expected(state: BuskingState): MinigameOutcome {
    return payout(state, 0);
  },
};
